import React, { useState, useEffect } from "react";
import CustomInput from "components/CustomInput/CustomInput";
import CustomButton from "components/CustomButton";
import logo from "assets/img/reteam_logo.png";
import * as Yup from "yup";
import { useFormik } from "formik";
import AuthService from "services/AuthService";
import { Link } from "react-router-dom";
import * as microsoftTeams from "@microsoft/teams-js";
import Loader from "components/Loader";
import "assets/scss/partials/authentication.scss";
import "assets/scss/base/custom.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPencilAlt,
  faLock,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import useCountDownTimer from "hooks/useCountDownTimer";

var styles = {
  emailImage: {
    width: "45%",
    maxWidth: "15%",
  },
  codeInformation: {
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    gap: "5px",
  },
  mailText: {
    fontFamily: "Roboto",
    color: "#3f51b5",
    fontWeight: "400",
    fontSize: "13px",
  },
  alertText: {
    color: "#f44336",
    fontWeight: "400",
    fontFamily: "Roboto",
    fontSize: "13px",
  },
  time: {
    color: "#fea000",
    fontSize: "25px",
    fontWeight: "500",
    padding: "15px 0px",
    fontFamily: "Roboto",
  },
  disableResend: {
    pointerEvents: "none",
    cursor: "default",
    color: "gray",
    margin: "3px",
  },
  enableResend: {
    pointerEvents: "all",
    cursor: "pointer",
    color: "#bf780e",
    textDecoration: "underline",
    margin: "3px",
  },
  recieveCodeText: {
    display: "flex",
    justifyContent: "center",
    padding: "10px 0px",
  },
};

export default () => {
  const [activeCard, setActiveCard] = useState("forgotPassword");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");

  useEffect(() => {
    microsoftTeams.app.initialize();
    microsoftTeams.app.notifySuccess();
    //eslint-disable-next-line
  }, []);

  return (
    <div className="root" id="user_login">
      <div className="inputBox">
        <div className="login_logo textcenter">
          <img className="logo" alt="ReTeam Logo" src={logo} />
        </div>
        {activeCard === "forgotPassword" && (
          <ForgetPasswordCard
            setActiveCard={setActiveCard}
            setEmail={setEmail}
          />
        )}
        {activeCard === "resetCode" && (
          <ResetCodeCard
            setActiveCard={setActiveCard}
            email={email}
            setToken={setToken}
          />
        )}
        {activeCard === "newPassword" && (
          <NewPasswordCard
            setActiveCard={setActiveCard}
            email={email}
            token={token}
          />
        )}
        {activeCard === "" && (
          <>
            <h4>You have successfully reset your password.</h4>
            <Link to="/login">Continue to Login</Link>
          </>
        )}
      </div>
    </div>
  );
};

const ForgetPasswordCard = ({ setActiveCard, setEmail }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      email: Yup.string().email().required(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await AuthService.forgotPassword(values.email);

        setEmail(values.email);
        setActiveCard("resetCode");
      } catch (error) {
        if (error.response) {
          setErrorState(error.response.data.message, setErrorMessage);
        }
      }
      setLoading(false);
    },
  });

  return (
    <div className="content">
      <div className="textcenter heading">
        Forgot Password?
        <p className="forgotPasswordText">
          Enter email and submit to receive a password reset code.
        </p>
      </div>
      <div className="formContent">
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className="inputFields">
            <CustomInput
              id={"email"}
              startIcon={
                <FontAwesomeIcon icon={faEnvelope} className="formIcon" />
              }
              inputProps={{
                type: "email",
                placeholder: "Enter Your Email",
                ...formik.getFieldProps("email"),
                error: formik.errors.email,
              }}
            />
          </div>
          {errorMessage && <div className="errorMessage">{errorMessage}</div>}
          <CustomButton
            name={
              !loading ? (
                "Send Recovery Email"
              ) : (
                <Loader width={40} height={40} />
              )
            }
            disabled={loading && true}
          />
        </form>
        <div className="textcenter">
          Remember your password? <Link to="/login">Login.</Link>
        </div>
      </div>
    </div>
  );
};

const ResetCodeCard = ({ setActiveCard, email, setToken }) => {
  const classes = styles;
  const [disableResendCode, setDisableResendCode] = useState(true);
  const { date } = useCountDownTimer({
    setDisableResendCode,
    disableResendCode,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const formik = useFormik({
    initialValues: {
      email,
      token: "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      email: Yup.string().email().required(),
      token: Yup.string().min(4).required(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await AuthService.verifyResetPasswordToken(values);

        setToken(values.token);
        setActiveCard("newPassword");
      } catch (error) {
        if (error.response) {
          setErrorState(error.response.data.message, setErrorMessage);
        }
      }
      setLoading(false);
    },
  });

  const resendCode = async (event) => {
    event.preventDefault();
    await AuthService.resendVerificationCode(email);
    setDisableResendCode(true);
  };

  return (
    <div className="content">
      <div className="textcenter heading">
        Forgot Password?
        <p className="forgotPasswordText">Enter reset code for "{email}"</p>
        <div style={classes.mailText}>
        Please check your inbox and spam too for the mail
        </div>
        <div style={classes.alertText}>
          If you haven't received the email please wait for 20 minutes.
        </div>
        <div style={classes.time}>{date}</div>
      </div>
      <div className="formContent">
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className="inputFields">
            <CustomInput
              startIcon={
                <FontAwesomeIcon icon={faPencilAlt} className="formIcon" />
              }
              id="beremote-password-reset-token"
              inputProps={{
                placeholder: "Enter Your Token Here",
                type: "text",
                ...formik.getFieldProps("token"),
                error: formik.errors.token,
              }}
            />
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
          </div>
          <CustomButton
            name={!loading ? "Continue" : <Loader width={40} height={40} />}
            disabled={loading && true}
          />
        </form>
        <div className="textcenter">
          Remember your password? <Link to="/login">Login.</Link>
        </div>
        <div style={classes.recieveCodeText}>
          <span>
            Didn't received code?{" "}
            <a
              href="/forgot-password"
              style={
                disableResendCode ? classes.disableResend : classes.enableResend
              }
              onClick={(event) => resendCode(event)}
            >
              Resend Code
            </a>
          </span>
        </div>
      </div>
    </div>
  );
};

const NewPasswordCard = ({ setActiveCard, email, token }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const formik = useFormik({
    initialValues: {
      password: "",
      password_confirmation: "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .matches(
          /^(?=.*[0-9])(?=.*[!=%^~():/<>;{}|_+@#$%^&*.,])(?=.*[a-zA-Z])/,
          "password must contain 8-20 characters including letters, numbers & special characters."
        )
        .min(8, "Password cannot be less then 8 words")
        .max(20, "Password cannot be greater than 20 words")
        .required("Password is required"),
      password_confirmation: Yup.string()
        .equals([Yup.ref("password")], "Passwords Must Match")
        .required("Password Confirmation is requried."),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);

        await AuthService.resetPassword(values, { token: token, email: email });

        setActiveCard("");
      } catch (error) {
        if (error.response) {
          setErrorState(error.response.data.message, setErrorMessage);
        }
      }
      setLoading(false);
    },
  });

  return (
    <div className="content">
      <div className="textcenter heading">
        Reset Password
        <p className="forgotPasswordText">Enter new password for "{email}"</p>
      </div>
      <div className="formContent">
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className="inputFields">
            <CustomInput
              id={"password"}
              startIcon={<FontAwesomeIcon icon={faLock} className="formIcon" />}
              inputProps={{
                placeholder: "Enter Your New Password",
                type: showPassword ? "text" : "password",
                ...formik.getFieldProps("password"),
                error: formik.errors.password,
              }}
              endIcon={
                <FontAwesomeIcon
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="toggle password visibility"
                  icon={!showPassword ? faEyeSlash : faEye}
                  id="togglePassword"
                  style={{ marginLeft: "-30px", cursor: "pointer" }}
                />
              }
            />
            <CustomInput
              id={"confirm-pwd"}
              startIcon={<FontAwesomeIcon icon={faLock} className="formIcon" />}
              inputProps={{
                type: showConfirmPassword ? "text" : "password",
                placeholder: "Confirm Your Password",
                ...formik.getFieldProps("password_confirmation"),
                error: formik.errors.password_confirmation,
              }}
              endIcon={
                <FontAwesomeIcon
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  icon={!showConfirmPassword ? faEyeSlash : faEye}
                  style={{ marginLeft: "-30px", cursor: "pointer" }}
                />
              }
            />
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
          </div>
          <CustomButton
            name={!loading ? "Continue" : <Loader width={40} height={40} />}
            disabled={loading && true}
          />
        </form>
        <div className="textcenter">
          Remember your password? <Link to="/login">Login.</Link>
        </div>
      </div>
    </div>
  );
};

const setErrorState = (message, setErrorMessage) => {
  setErrorMessage(message);
  setTimeout((_) => {
    setErrorMessage("");
  }, 4000);
};
